'use client';

import {
  CountryInput,
  Input,
  InputErrorMessage,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { Country } from '@prisma/client';
import { useCallback } from 'react';
import { useBusinessEntityFields } from './hooks/useBusinessEntityFields';
import {
  BusinessEntityCreationFieldsProps,
  BusinessEntityFormData,
} from './types';

/**
 * BusinessEntityCreationFields Component
 *
 * Renders the required fields for creating a new business entity:
 * - Legal Name (always enabled)
 * - Country (with flags, priority countries first)
 * - Jurisdiction (disabled until country selected, cascades from country)
 * - Entity Type (disabled until country and jurisdiction selected, cascades from both)
 *
 * Features:
 * - Cascading dropdown logic with auto-select when single option
 * - Validation error display
 * - Disabled states for dependent fields
 */
export const BusinessEntityCreationFields = ({
  entityId,
  parentFieldId,
  formData,
  onChange,
  disabled = false,
  errors = {},
}: BusinessEntityCreationFieldsProps) => {
  const {
    countryOptions,
    getJurisdictionOptions,
    getEntityTypeOptions,
    getCorporationTypeForLegalForm,
    isJurisdictionEnabled,
    isEntityTypeEnabled,
    getAutoSelectJurisdiction,
    getAutoSelectEntityLegalFormId,
    isLoading,
  } = useBusinessEntityFields();

  // Handle country change with cascading logic
  const handleCountryChange = useCallback(
    (value: string | null) => {
      const country = value as Country | null;

      // Reset dependent fields when country changes
      const updates: Partial<BusinessEntityFormData> = {
        incorporationCountry: country,
        jurisdiction: null,
        businessEntityType: null,
        entityLegalFormId: null,
      };

      // Auto-select jurisdiction if only one option
      if (country) {
        const autoJurisdiction = getAutoSelectJurisdiction(country);
        if (autoJurisdiction) {
          updates.jurisdiction = autoJurisdiction;

          // Also try to auto-select entity type
          const autoLegalFormId = getAutoSelectEntityLegalFormId(
            country,
            autoJurisdiction
          );
          if (autoLegalFormId) {
            updates.entityLegalFormId = autoLegalFormId;
            updates.businessEntityType = getCorporationTypeForLegalForm(
              country,
              autoJurisdiction,
              autoLegalFormId
            );
          }
        }
      }

      onChange(updates);
    },
    [
      onChange,
      getAutoSelectJurisdiction,
      getAutoSelectEntityLegalFormId,
      getCorporationTypeForLegalForm,
    ]
  );

  // Handle jurisdiction change with cascading logic
  const handleJurisdictionChange = useCallback(
    (value: string) => {
      const jurisdiction = value;

      // Reset entity type when jurisdiction changes
      const updates: Partial<BusinessEntityFormData> = {
        jurisdiction,
        businessEntityType: null,
        entityLegalFormId: null,
      };

      // Auto-select entity type if only one option
      if (formData.incorporationCountry) {
        const autoLegalFormId = getAutoSelectEntityLegalFormId(
          formData.incorporationCountry,
          jurisdiction
        );
        if (autoLegalFormId) {
          updates.entityLegalFormId = autoLegalFormId;
          updates.businessEntityType = getCorporationTypeForLegalForm(
            formData.incorporationCountry,
            jurisdiction,
            autoLegalFormId
          );
        }
      }

      onChange(updates);
    },
    [
      onChange,
      formData.incorporationCountry,
      getAutoSelectEntityLegalFormId,
      getCorporationTypeForLegalForm,
    ]
  );

  // Handle entity type change
  const handleEntityTypeChange = useCallback(
    (value: string) => {
      onChange({
        entityLegalFormId: value,
        businessEntityType: getCorporationTypeForLegalForm(
          formData.incorporationCountry,
          formData.jurisdiction,
          value
        ),
      });
    },
    [
      onChange,
      formData.incorporationCountry,
      formData.jurisdiction,
      getCorporationTypeForLegalForm,
    ]
  );

  // Handle legal name change
  const handleLegalNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ legalName: e.target.value });
    },
    [onChange]
  );

  // Get options for dependent dropdowns
  const jurisdictionOptions = getJurisdictionOptions(
    formData.incorporationCountry
  );
  const entityTypeOptions = getEntityTypeOptions(
    formData.incorporationCountry,
    formData.jurisdiction
  );

  // Determine disabled states
  const jurisdictionDisabled =
    disabled || !isJurisdictionEnabled(formData.incorporationCountry);
  const entityTypeDisabled =
    disabled ||
    !isEntityTypeEnabled(formData.incorporationCountry, formData.jurisdiction);

  const fieldIdPrefix = `${parentFieldId}-entity-${entityId}`;

  return (
    <div className="space-y-4">
      {/* Legal Name */}
      <div className="space-y-1.5">
        <Label htmlFor={`${fieldIdPrefix}-legalName`}>
          <span>Legal Name</span>
          <span className="text-utility-violet-600 ml-0.5">*</span>
        </Label>
        <Input
          id={`${fieldIdPrefix}-legalName`}
          value={formData.legalName}
          onChange={handleLegalNameChange}
          placeholder="Enter legal name"
          disabled={disabled}
          error={errors.legalName}
        />
        {/* Input component already displays error internally */}
      </div>

      {/* Country */}
      <div className="space-y-1.5">
        <Label htmlFor={`${fieldIdPrefix}-country`}>
          <span>Country</span>
          <span className="text-utility-violet-600 ml-0.5">*</span>
        </Label>
        <CountryInput
          id={`${fieldIdPrefix}-country`}
          type="single"
          value={formData.incorporationCountry}
          onValueChange={handleCountryChange}
          placeholder="Select country"
          disabled={disabled || isLoading}
          error={errors.incorporationCountry}
        />
        {/* CountryInput component already displays error internally */}
      </div>

      {/* Jurisdiction */}
      <div className="space-y-1.5">
        <Label htmlFor={`${fieldIdPrefix}-jurisdiction`}>
          <span>Jurisdiction</span>
          <span className="text-utility-violet-600 ml-0.5">*</span>
        </Label>
        <Select
          value={formData.jurisdiction || ''}
          onValueChange={handleJurisdictionChange}
          disabled={jurisdictionDisabled}
        >
          <SelectTrigger
            id={`${fieldIdPrefix}-jurisdiction`}
            error={errors.jurisdiction}
            disabled={jurisdictionDisabled}
            className={cn(
              jurisdictionDisabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <SelectValue
              placeholder={
                !formData.incorporationCountry
                  ? 'Select country first'
                  : 'Select jurisdiction'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {jurisdictionOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.jurisdiction && (
          <InputErrorMessage message={errors.jurisdiction} />
        )}
      </div>

      {/* Entity Type */}
      <div className="space-y-1.5">
        <Label htmlFor={`${fieldIdPrefix}-entityType`}>
          <span>Entity Type</span>
          <span className="text-utility-violet-600 ml-0.5">*</span>
        </Label>
        <Select
          value={formData.entityLegalFormId || ''}
          onValueChange={handleEntityTypeChange}
          disabled={entityTypeDisabled}
        >
          <SelectTrigger
            id={`${fieldIdPrefix}-entityType`}
            error={errors.businessEntityType}
            disabled={entityTypeDisabled}
            className={cn(
              entityTypeDisabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <SelectValue
              placeholder={
                !formData.incorporationCountry
                  ? 'Select country first'
                  : !formData.jurisdiction
                    ? 'Select jurisdiction first'
                    : 'Select entity type'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {entityTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.businessEntityType && (
          <InputErrorMessage message={errors.businessEntityType} />
        )}
      </div>
    </div>
  );
};
